import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { calculateNextReviewDate, calculateMasteryLevel } from '@/lib/mistake-analyzer';
import type { TrainingPosition } from './useTrainingPositions';

export interface TrainingAttempt {
  positionId: string;
  userMove: string;
  isCorrect: boolean;
  timeSpent: number;
}

export function useTrainingSession(sessionPositions: TrainingPosition[]) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attempts, setAttempts] = useState<TrainingAttempt[]>([]);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isComplete, setIsComplete] = useState(false);

  const currentPosition = sessionPositions[currentIndex];

  useEffect(() => {
    setStartTime(Date.now());
  }, [currentIndex]);

  const recordAttempt = useCallback(async (
    userMoveUci: string,
    userMoveSan: string,
    isCorrect: boolean
  ) => {
    if (!currentPosition) return;

    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    // Record attempt in database
    try {
      const { error } = await (supabase as any).from('training_attempts').insert({
        training_position_id: currentPosition.id,
        user_id: currentPosition.user_id,
        user_move_uci: userMoveUci,
        user_move_san: userMoveSan,
        is_correct: isCorrect,
        time_spent_seconds: timeSpent,
        session_id: sessionId,
        attempt_number: currentPosition.times_attempted + 1
      });

      if (error) throw error;

      // Update position stats
      const newTimesAttempted = currentPosition.times_attempted + 1;
      const newTimesSolved = currentPosition.times_solved + (isCorrect ? 1 : 0);
      const newMasteryLevel = calculateMasteryLevel(newTimesAttempted, newTimesSolved);

      // Calculate next review date using spaced repetition
      const currentInterval = Math.max(1, 
        Math.floor((new Date(currentPosition.next_review_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      );
      const { nextDate, newEaseFactor } = calculateNextReviewDate(
        isCorrect,
        currentPosition.ease_factor,
        currentInterval
      );

      await (supabase as any)
        .from('training_positions')
        .update({
          times_attempted: newTimesAttempted,
          times_solved: newTimesSolved,
          mastery_level: newMasteryLevel,
          last_attempted_at: new Date().toISOString(),
          next_review_date: nextDate.toISOString(),
          ease_factor: newEaseFactor
        })
        .eq('id', currentPosition.id);

      setAttempts(prev => [...prev, {
        positionId: currentPosition.id,
        userMove: userMoveSan,
        isCorrect,
        timeSpent
      }]);

    } catch (error) {
      console.error('Error recording attempt:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your attempt',
        variant: 'destructive',
      });
    }
  }, [currentPosition, startTime, sessionId]);

  const nextPosition = useCallback(() => {
    if (currentIndex < sessionPositions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  }, [currentIndex, sessionPositions.length]);

  const previousPosition = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const skipPosition = useCallback(() => {
    nextPosition();
  }, [nextPosition]);

  const getSessionStats = useCallback(() => {
    const correct = attempts.filter(a => a.isCorrect).length;
    const total = attempts.length;
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
    const avgTime = total > 0 
      ? Math.round(attempts.reduce((sum, a) => sum + a.timeSpent, 0) / total)
      : 0;

    return { correct, total, accuracy, avgTime };
  }, [attempts]);

  return {
    currentPosition,
    currentIndex,
    totalPositions: sessionPositions.length,
    attempts,
    isComplete,
    recordAttempt,
    nextPosition,
    previousPosition,
    skipPosition,
    getSessionStats
  };
}
